import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  Statistic,
  Table,
  Progress,
  Tag,
  Avatar,
  List,
  Typography,
  Space,
  Button,
  Select,
  DatePicker,
  Alert,
  Spin,
  Divider,
  Tooltip as AntTooltip,
  Badge,
  App
} from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  BankOutlined,
  CrownOutlined,
  DashboardOutlined,
  RiseOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  CalendarOutlined,
  BarChartOutlined,
  PieChartOutlined,
  LineChartOutlined,
  ReloadOutlined,
  ExportOutlined
} from '@ant-design/icons';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import axios from 'axios';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const Dashboard = () => {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    stats: {},
    recentActivity: [],
    systemHealth: {},
    analytics: {},
    topOrganizations: [],
    userGrowth: [],
    subscriptionStats: []
  });
  const [selectedPeriod, setSelectedPeriod] = useState('30d');
  const [refreshing, setRefreshing] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    fetchDashboardData();
  }, [selectedPeriod]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('super_admin_token');
      
      const [statsRes, healthRes, analyticsRes, activityRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/dashboard/health`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/dashboard/analytics?period=${selectedPeriod}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/dashboard/activity`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setDashboardData({
        stats: statsRes.data.data || {},
        systemHealth: healthRes.data.data || {},
        analytics: analyticsRes.data.data || {},
        recentActivity: activityRes.data.data || [],
        topOrganizations: analyticsRes.data.data?.topOrganizations || [],
        userGrowth: analyticsRes.data.data?.userGrowth || [],
        subscriptionStats: analyticsRes.data.data?.subscriptionStats || []
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardData();
    setRefreshing(false);
  };

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
  };

  // Chart colors
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];
  
  // Process user growth data for line chart
  const processUserGrowthData = () => {
    const dataMap = {};
    dashboardData.userGrowth.forEach(item => {
      if (!dataMap[item.date]) {
        dataMap[item.date] = { date: item.date };
      }
      dataMap[item.date][item.type] = item.count;
    });
    return Object.values(dataMap);
  };

  const organizationColumns = [
    {
      title: 'Organization',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <Avatar icon={<BankOutlined />} style={{ backgroundColor: '#1890ff' }} />
          <div>
            <div style={{ fontWeight: 500 }}>{text}</div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.subdomain}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'Users',
      dataIndex: 'totalUsers',
      key: 'totalUsers',
      render: (count) => (
        <Badge count={count} style={{ backgroundColor: '#52c41a' }} />
      ),
    },
    {
      title: 'Plan',
      dataIndex: 'plan',
      key: 'plan',
      render: (plan) => {
        const colors = {
          free: 'default',
          basic: 'blue',
          premium: 'gold',
          enterprise: 'purple'
        };
        return <Tag color={colors[plan] || 'default'}>{plan?.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag color={isActive ? 'success' : 'error'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Revenue',
      dataIndex: 'monthlyRevenue',
      key: 'monthlyRevenue',
      render: (revenue) => (
        <Text strong style={{ color: '#52c41a' }}>
          ${revenue || 0}
        </Text>
      ),
    }
  ];

  const activityColumns = [
    {
      title: 'Activity',
      dataIndex: 'action',
      key: 'action',
      render: (action, record) => (
        <Space>
          <Avatar 
            size="small" 
            icon={<UserOutlined />} 
            style={{ backgroundColor: getActivityColor(record.type) }}
          />
          <div>
            <div>{action}</div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.details}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'User',
      dataIndex: 'userName',
      key: 'userName',
    },
    {
      title: 'Time',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp) => (
        <Text type="secondary">
          {new Date(timestamp).toLocaleString()}
        </Text>
      ),
    }
  ];

  const getActivityColor = (type) => {
    const colors = {
      user_created: '#52c41a',
      user_login: '#1890ff',
      organization_created: '#722ed1',
      subscription_changed: '#faad14',
      system_alert: '#f5222d'
    };
    return colors[type] || '#d9d9d9';
  };

  const getHealthStatus = (status) => {
    switch (status) {
      case 'healthy':
        return { color: 'success', icon: <CheckCircleOutlined /> };
      case 'warning':
        return { color: 'warning', icon: <WarningOutlined /> };
      case 'critical':
        return { color: 'error', icon: <CloseCircleOutlined /> };
      default:
        return { color: 'default', icon: <CheckCircleOutlined /> };
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '60vh' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px' 
      }}>
        <div>
          <Title level={2} style={{ margin: 0 }}>
            <DashboardOutlined /> Super Admin Dashboard
          </Title>
          <Text type="secondary">
            System overview and analytics
          </Text>
        </div>
        <Space>
          <Select
            value={selectedPeriod}
            onChange={handlePeriodChange}
            style={{ width: 120 }}
          >
            <Option value="7d">Last 7 days</Option>
            <Option value="30d">Last 30 days</Option>
            <Option value="90d">Last 3 months</Option>
            <Option value="1y">Last year</Option>
          </Select>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={handleRefresh}
            loading={refreshing}
          >
            Refresh
          </Button>
          <Button icon={<ExportOutlined />} type="primary">
            Export Report
          </Button>
        </Space>
      </div>

      {/* System Health Alert */}
      {dashboardData.systemHealth?.status === 'warning' && (
        <Alert
          message="System Warning"
          description={dashboardData.systemHealth.message}
          type="warning"
          showIcon
          closable
          style={{ marginBottom: '24px' }}
        />
      )}

      {/* Key Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Organizations"
              value={dashboardData.stats.totalOrganizations || 0}
              prefix={<BankOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
            <div style={{ marginTop: '8px' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                +{dashboardData.stats.newOrganizations || 0} this month
              </Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Users"
              value={dashboardData.stats.totalUsers || 0}
              prefix={<TeamOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
            <div style={{ marginTop: '8px' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                +{dashboardData.stats.newUsers || 0} this month
              </Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Active Subscriptions"
              value={dashboardData.stats.activeSubscriptions || 0}
              prefix={<CrownOutlined style={{ color: '#faad14' }} />}
              valueStyle={{ color: '#faad14' }}
            />
            <div style={{ marginTop: '8px' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {dashboardData.stats.subscriptionGrowth || 0}% growth
              </Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Monthly Revenue"
              value={dashboardData.stats.monthlyRevenue || 0}
              prefix={<DollarOutlined style={{ color: '#f5222d' }} />}
              valueStyle={{ color: '#f5222d' }}
              precision={2}
            />
            <div style={{ marginTop: '8px' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                +{dashboardData.stats.revenueGrowth || 0}% from last month
              </Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Charts Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={16}>
          <Card 
            title={
              <Space>
                <LineChartOutlined />
                User Growth Trend
              </Space>
            }
            extra={
              <Tag color="blue">{selectedPeriod.toUpperCase()}</Tag>
            }
          >
            {dashboardData.userGrowth.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={processUserGrowthData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="Admins" stroke="#ff7300" strokeWidth={2} />
                  <Line type="monotone" dataKey="Users" stroke="#387908" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ 
                height: '300px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <Text type="secondary">No data available</Text>
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card 
            title={
              <Space>
                <PieChartOutlined />
                Subscription Distribution
              </Space>
            }
          >
            {dashboardData.subscriptionStats.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={dashboardData.subscriptionStats}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {dashboardData.subscriptionStats.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ 
                height: '300px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <Text type="secondary">No data available</Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Data Tables Row */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <Card 
            title={
              <Space>
                <BarChartOutlined />
                Top Organizations
              </Space>
            }
            extra={
              <Button type="link" size="small">
                View All
              </Button>
            }
          >
            <Table
              columns={organizationColumns}
              dataSource={dashboardData.topOrganizations}
              pagination={{ pageSize: 5 }}
              size="small"
              rowKey="_id"
            />
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card 
            title={
              <Space>
                <CalendarOutlined />
                Recent Activity
              </Space>
            }
            extra={
              <Button type="link" size="small">
                View All
              </Button>
            }
          >
            <Table
              columns={activityColumns}
              dataSource={dashboardData.recentActivity}
              pagination={{ pageSize: 5 }}
              size="small"
              rowKey="id"
              showHeader={false}
            />
          </Card>
        </Col>
      </Row>

      {/* System Health */}
      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        <Col span={24}>
          <Card 
            title={
              <Space>
                <RiseOutlined />
                System Health
              </Space>
            }
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}>
                <Card size="small">
                  <Statistic
                    title="Database"
                    value={dashboardData.systemHealth?.database?.responseTime || 0}
                    suffix="ms"
                    prefix={
                      <Tag {...getHealthStatus(dashboardData.systemHealth?.database?.status)}>
                        {getHealthStatus(dashboardData.systemHealth?.database?.status).icon}
                      </Tag>
                    }
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card size="small">
                  <Statistic
                    title="API Response"
                    value={dashboardData.systemHealth?.api?.responseTime || 0}
                    suffix="ms"
                    prefix={
                      <Tag {...getHealthStatus(dashboardData.systemHealth?.api?.status)}>
                        {getHealthStatus(dashboardData.systemHealth?.api?.status).icon}
                      </Tag>
                    }
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card size="small">
                  <Statistic
                    title="Memory Usage"
                    value={dashboardData.systemHealth?.memory?.usage || 0}
                    suffix="%"
                    prefix={
                      <Tag {...getHealthStatus(dashboardData.systemHealth?.memory?.status)}>
                        {getHealthStatus(dashboardData.systemHealth?.memory?.status).icon}
                      </Tag>
                    }
                  />
                </Card>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
